(() => {
  const CINEMA_ACTIVE_CLASS = 'hagenuk-cinema-active';
  const CINEMA_START_EVENT = 'hagenuk:cinema-start';
  const CINEMA_COMPLETE_EVENT = 'hagenuk:cinema-complete';

  class HagenukHeroGallery {
    constructor(root) {
      this.root = root;
      this.images = Array.from(root.querySelectorAll('[data-hagenuk-hero-image]'));
      this.progress = Array.from(root.querySelectorAll('[data-hagenuk-hero-progress]'));
      this.products = Array.from(root.querySelectorAll('[data-hagenuk-launch-product]'));
      this.video = root.querySelector('[data-hagenuk-launch-video] video');
      this.reel = root.querySelector('[data-hagenuk-launch-reel]');
      this.reelVideos = Array.from(root.querySelectorAll('[data-hagenuk-launch-reel-video]'));
      this.reelControls = Array.from(root.querySelectorAll('[data-hagenuk-launch-reel-control]'));
      this.reelCounter = root.querySelector('[data-hagenuk-launch-reel-counter]');
      this.videoMode = root.dataset.videoMode || 'intro';
      this.interval = Math.max(3600, Number(root.dataset.interval) || 5200);
      this.transitionDuration = Math.max(900, Number(root.dataset.transitionDuration) || 1400);

      this.current = 0;
      this.timer = 0;
      this.transitionTimer = 0;
      this.videoFallbackTimer = 0;
      this.videoStallTimer = 0;
      this.reelLoadTimer = 0;
      this.imagePreloadTimer = 0;
      this.secondaryImagePreloadTimer = 0;
      this.transitioning = false;
      this.videoActive = false;
      this.videoPending = false;
      this.videoSetup = false;
      this.videoFinished = false;
      this.reelActive = false;
      this.reelReady = false;
      this.reelIndex = 0;
      this.visible = true;
      this.mediaInitialized = false;
      this.cinemaWaiting = document.documentElement.classList.contains(CINEMA_ACTIVE_CLASS);
      this.destroyed = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.mobileDevice = window.matchMedia('(max-width: 740px), (pointer: coarse)').matches;
      this.lowPower = (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ||
        (navigator.deviceMemory && navigator.deviceMemory <= 4);
      this.stillOnly = this.reducedMotion || this.saveData;
      this.allowReel = !this.stillOnly && !(this.mobileDevice && this.lowPower);

      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onVideoPlaying = this.onVideoPlaying.bind(this);
      this.onVideoEnded = this.onVideoEnded.bind(this);
      this.onVideoFailure = this.onVideoFailure.bind(this);
      this.onVideoWaiting = this.onVideoWaiting.bind(this);
      this.onReelPlaying = this.onReelPlaying.bind(this);
      this.onReelEnded = this.onReelEnded.bind(this);
      this.onReelFailure = this.onReelFailure.bind(this);
      this.onReelControlClick = this.onReelControlClick.bind(this);
      this.onCinemaStart = this.onCinemaStart.bind(this);
      this.onCinemaComplete = this.onCinemaComplete.bind(this);
    }

    init() {
      if (this.images.length < 2 || this.root.dataset.galleryReady === 'true') return;

      this.root.dataset.galleryReady = 'true';
      this.root.style.setProperty('--hagenuk-gallery-transition', `${this.transitionDuration}ms`);
      this.syncProducts(0);

      if (!this.isCinemaBlocked()) this.preloadImages();

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.sync();
        }, { threshold: 0.2 });
        this.intersectionObserver.observe(this.root);
      } else {
        this.visible = true;
      }

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener(CINEMA_START_EVENT, this.onCinemaStart);
      document.addEventListener(CINEMA_COMPLETE_EVENT, this.onCinemaComplete);

      this.startMediaWhenReady();
      this.sync();
    }

    isCinemaBlocked() {
      return this.cinemaWaiting || document.documentElement.classList.contains(CINEMA_ACTIVE_CLASS);
    }

    preloadImages() {
      if (this.destroyed || this.isCinemaBlocked() || this.saveData) return;
      this.load(1);
      window.clearTimeout(this.imagePreloadTimer);
      if (!this.lowPower && !this.mobileDevice) {
        this.imagePreloadTimer = window.setTimeout(() => {
          if (!this.destroyed && !this.isCinemaBlocked()) this.load(2);
        }, 1100);
      }
    }

    startMediaWhenReady() {
      if (this.destroyed || this.mediaInitialized || this.isCinemaBlocked()) return;

      this.mediaInitialized = true;
      if (this.stillOnly) return;
      if (this.video) {
        this.setupVideo();
      } else if (this.allowReel) {
        this.setupReel();
      }
    }

    onCinemaStart() {
      if (this.destroyed) return;

      this.cinemaWaiting = true;
      window.clearTimeout(this.videoFallbackTimer);
      window.clearTimeout(this.videoStallTimer);
      window.clearTimeout(this.reelLoadTimer);
      window.clearTimeout(this.imagePreloadTimer);
      window.clearTimeout(this.secondaryImagePreloadTimer);

      if (this.video) this.video.pause();
      this.reelVideos.forEach((video) => video.pause());

      this.videoPending = false;
      this.videoActive = false;
      this.reelActive = false;
      this.root.classList.remove('has-active-video', 'has-active-reel');
      this.reel?.classList.remove('is-playing');

      // Keep preloaded stills ready, but avoid image transitions/compositor work
      // while the full-screen cinematic layer is active.
      window.clearTimeout(this.timer);
    }

    onCinemaComplete() {
      if (this.destroyed) return;

      this.cinemaWaiting = false;
      this.preloadImages();
      this.startMediaWhenReady();

      if (this.videoSetup && !this.videoFinished && !this.reducedMotion) {
        this.playHeroVideo();
      } else if (this.videoFinished && !this.reelReady && this.allowReel) {
        this.setupReel();
      }

      this.sync();
    }

    setupVideo() {
      if (!this.video || this.videoSetup || this.isCinemaBlocked()) return;

      this.videoSetup = true;
      this.videoFinished = false;
      this.video.muted = true;
      this.video.playsInline = true;
      this.video.addEventListener('playing', this.onVideoPlaying);
      this.video.addEventListener('ended', this.onVideoEnded);
      this.video.addEventListener('error', this.onVideoFailure);
      this.video.addEventListener('abort', this.onVideoFailure);
      this.video.addEventListener('waiting', this.onVideoWaiting);
      this.video.addEventListener('stalled', this.onVideoWaiting);
      this.playHeroVideo();
    }

    playHeroVideo() {
      if (
        !this.video ||
        !this.videoSetup ||
        this.videoFinished ||
        this.videoPending ||
        this.videoActive ||
        this.stillOnly ||
        this.isCinemaBlocked()
      ) return;

      this.videoPending = true;
      window.clearTimeout(this.videoFallbackTimer);
      window.clearTimeout(this.videoStallTimer);
      this.videoFallbackTimer = window.setTimeout(() => {
        if (!this.videoActive) this.onVideoFailure();
      }, 3200);
      const playback = this.video.play();
      if (playback && typeof playback.catch === 'function') playback.catch(this.onVideoFailure);
    }

    onVideoPlaying() {
      if (this.isCinemaBlocked()) {
        this.video?.pause();
        this.videoPending = false;
        this.videoActive = false;
        return;
      }

      if (!this.visible || document.hidden) {
        window.clearTimeout(this.videoFallbackTimer);
        window.clearTimeout(this.videoStallTimer);
        this.video?.pause();
        this.videoPending = false;
        this.videoActive = true;
        return;
      }

      window.clearTimeout(this.videoFallbackTimer);
      window.clearTimeout(this.videoStallTimer);
      this.videoPending = false;
      this.videoActive = true;
      this.root.classList.add('has-active-video');
      this.pauseReel();
      window.clearTimeout(this.timer);
    }

    onVideoEnded() {
      window.clearTimeout(this.videoStallTimer);
      if (this.videoMode === 'loop' && !this.isCinemaBlocked()) {
        this.videoActive = false;
        const playback = this.video.play();
        if (playback && typeof playback.catch === 'function') playback.catch(this.onVideoFailure);
        return;
      }

      this.videoPending = false;
      this.videoActive = false;
      this.videoFinished = true;
      this.root.classList.remove('has-active-video');
      if (this.allowReel) this.setupReel();
      this.sync();
    }

    onVideoFailure() {
      window.clearTimeout(this.videoFallbackTimer);
      window.clearTimeout(this.videoStallTimer);
      this.videoPending = false;
      this.videoActive = false;
      this.root.classList.remove('has-active-video');
      if (this.destroyed || this.isCinemaBlocked()) return;
      this.videoFinished = true;
      if (this.allowReel) this.setupReel();
      this.sync();
    }

    onVideoWaiting() {
      if (this.destroyed || this.isCinemaBlocked() || !this.videoActive) return;
      window.clearTimeout(this.videoStallTimer);
      this.videoStallTimer = window.setTimeout(() => {
        if (!this.destroyed && !this.isCinemaBlocked() && this.video && this.video.readyState < 3) {
          this.onVideoFailure();
        }
      }, 4500);
    }

    setupReel() {
      if (!this.allowReel || !this.reel || !this.reelVideos.length || this.reelReady || this.isCinemaBlocked()) return;

      this.reelReady = true;
      this.reelVideos.forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.addEventListener('playing', this.onReelPlaying);
        video.addEventListener('ended', this.onReelEnded);
        video.addEventListener('error', this.onReelFailure);
        video.addEventListener('abort', this.onReelFailure);
      });
      this.reelControls.forEach((control) => {
        control.addEventListener('click', this.onReelControlClick);
      });

      window.requestAnimationFrame(() => this.reel.classList.add('is-ready'));
      this.activateReel(0, { play: !this.reducedMotion });
      this.reelLoadTimer = window.setTimeout(() => this.loadReel(1), 1200);
    }

    loadReel(index) {
      const video = this.reelVideos[index];
      if (!video || this.isCinemaBlocked()) return video || null;

      if (!video.getAttribute('src') && video.dataset.src) {
        video.src = video.dataset.src;
        video.removeAttribute('data-src');
        video.load();
      }
      return video;
    }

    activateReel(index, { play = true, restart = false } = {}) {
      if (!this.reelVideos.length) return;

      const nextIndex = ((Number(index) || 0) + this.reelVideos.length) % this.reelVideos.length;
      const previousVideo = this.reelVideos[this.reelIndex];
      const nextVideo = this.loadReel(nextIndex);

      if (previousVideo && (previousVideo !== nextVideo || restart)) {
        previousVideo.pause();
        if (restart) previousVideo.currentTime = 0;
      }
      if (nextVideo?.ended) nextVideo.currentTime = 0;

      this.reelVideos.forEach((video, videoIndex) => {
        video.classList.toggle('is-active', videoIndex === nextIndex);
      });
      this.reelControls.forEach((control, controlIndex) => {
        const active = controlIndex === nextIndex;
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-pressed', String(active));
      });

      this.reelIndex = nextIndex;
      this.root.dataset.hagenukReelIndex = String(nextIndex);
      if (this.reelCounter) {
        this.reelCounter.textContent = `${String(nextIndex + 1).padStart(2, '0')} / ${String(this.reelVideos.length).padStart(2, '0')}`;
      }

      if (play && !this.reducedMotion && !this.isCinemaBlocked()) {
        this.playReel();
      } else {
        this.pauseReel();
      }
    }

    playReel() {
      const video = this.reelVideos[this.reelIndex];
      if (
        !video ||
        this.videoActive ||
        !this.visible ||
        document.hidden ||
        this.reducedMotion ||
        this.isCinemaBlocked()
      ) return;

      this.reelActive = true;
      this.reel?.classList.add('is-playing');
      this.root.classList.add('has-active-reel');
      window.clearTimeout(this.timer);
      if (video.paused || video.ended) {
        const playback = video.play();
        if (playback && typeof playback.catch === 'function') playback.catch(this.onReelFailure);
      }
    }

    pauseReel() {
      this.reelVideos.forEach((video) => video.pause());
      this.reelActive = false;
      this.reel?.classList.remove('is-playing');
      this.root.classList.remove('has-active-reel');
    }

    onReelPlaying(event) {
      if (event.currentTarget !== this.reelVideos[this.reelIndex]) return;
      if (this.isCinemaBlocked()) {
        event.currentTarget.pause();
        this.pauseReel();
        return;
      }

      this.reelActive = true;
      this.reel?.classList.remove('is-fallback');
      this.reel?.classList.add('is-playing');
      this.root.classList.add('has-active-reel');

      const duration = Number(event.currentTarget.duration);
      if (Number.isFinite(duration) && duration > 0) {
        this.root.style.setProperty('--hagenuk-gallery-duration', `${Math.max(3600, duration * 1000)}ms`);
        const activeProgress = this.progress[this.current];
        activeProgress?.classList.remove('is-active');
        if (activeProgress) {
          activeProgress.offsetWidth;
          activeProgress.classList.add('is-active');
        }
      }
    }

    onReelEnded(event) {
      if (event.currentTarget !== this.reelVideos[this.reelIndex] || this.isCinemaBlocked()) return;

      const nextIndex = (this.reelIndex + 1) % this.reelVideos.length;
      this.activateReel(nextIndex);
      this.next('reel');
    }

    onReelFailure(event) {
      if (event?.currentTarget && event.currentTarget !== this.reelVideos[this.reelIndex]) return;

      this.pauseReel();
      if (!this.isCinemaBlocked()) this.reel?.classList.add('is-fallback');
      this.schedule();
    }

    onReelControlClick(event) {
      const index = Number(event.currentTarget.dataset.hagenukLaunchReelControl);
      if (!Number.isFinite(index)) return;

      this.activateReel(index, {
        play: !this.reducedMotion,
        restart: index === this.reelIndex
      });
      if (!this.reducedMotion) this.next('control');
    }

    async load(index) {
      const image = this.images[index];
      if (!image) return false;

      if (!image.getAttribute('src') && image.dataset.src) {
        image.loading = 'eager';
        image.src = image.dataset.src;
      }
      if (image.complete) {
        const valid = image.naturalWidth > 0;
        if (valid) image.removeAttribute('data-src');
        return valid;
      }

      const valid = await new Promise((resolve) => {
        let settled = false;
        const finish = (result) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onError);
          resolve(result);
        };
        const onLoad = () => finish(image.naturalWidth > 0);
        const onError = () => finish(false);
        const timeout = window.setTimeout(() => finish(image.complete && image.naturalWidth > 0), 6000);
        image.addEventListener('load', onLoad, { once: true });
        image.addEventListener('error', onError, { once: true });
      });

      if (valid) image.removeAttribute('data-src');
      return valid;
    }

    async next(source = 'gallery') {
      if (this.destroyed || this.isCinemaBlocked() || this.transitioning) return;

      this.transitioning = true;
      const nextIndex = (this.current + 1) % this.images.length;
      const imageReady = await this.load(nextIndex);
      if (!imageReady) {
        this.transitioning = false;
        this.schedule();
        return;
      }
      if (this.destroyed || this.isCinemaBlocked()) {
        this.transitioning = false;
        return;
      }

      const previousImage = this.images[this.current];
      const nextImage = this.images[nextIndex];
      nextImage.classList.remove('is-active', 'is-outgoing');
      nextImage.classList.add('is-entering');
      await new Promise((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
      });

      previousImage.classList.add('is-outgoing');
      nextImage.classList.add('is-active');
      this.progress[this.current]?.classList.remove('is-active');
      this.progress[nextIndex]?.classList.add('is-active');
      this.current = nextIndex;
      this.syncProducts(nextIndex);
      this.root.dispatchEvent(new CustomEvent('hagenuk:hero-change', {
        detail: { index: nextIndex, count: this.images.length, source }
      }));

      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = window.setTimeout(() => {
        previousImage.classList.remove('is-active', 'is-outgoing');
        nextImage.classList.remove('is-entering');
        this.transitioning = false;
      }, this.transitionDuration + 80);

      if (!this.saveData) this.load((nextIndex + 1) % this.images.length);
      if (!this.saveData && !this.lowPower && !this.mobileDevice) {
        window.clearTimeout(this.secondaryImagePreloadTimer);
        this.secondaryImagePreloadTimer = window.setTimeout(() => {
          if (!this.destroyed && !this.isCinemaBlocked()) this.load((nextIndex + 2) % this.images.length);
        }, 900);
      }
      this.schedule();
    }

    syncProducts(index) {
      this.products.forEach((product, productIndex) => {
        product.classList.toggle('is-active', productIndex === index % this.products.length);
      });
    }

    schedule() {
      window.clearTimeout(this.timer);
      if (
        !this.isCinemaBlocked() &&
        !this.stillOnly &&
        !this.videoActive &&
        !this.videoPending &&
        !this.reelActive &&
        this.visible &&
        !document.hidden
      ) {
        this.timer = window.setTimeout(() => this.next(), this.interval);
      }
    }

    sync() {
      if (this.isCinemaBlocked()) {
        if (this.video) this.video.pause();
        this.pauseReel();
        this.videoPending = false;
        this.videoActive = false;
        this.schedule();
        return;
      }

      if (!this.visible || document.hidden) {
        if (this.videoActive && this.video) this.video.pause();
        this.pauseReel();
        window.clearTimeout(this.timer);
        return;
      }

      this.startMediaWhenReady();
      if (this.videoActive || this.videoPending) {
        this.pauseReel();
        window.clearTimeout(this.timer);
        if (this.videoActive && this.video?.paused) {
          const playback = this.video.play();
          if (playback && typeof playback.catch === 'function') playback.catch(this.onVideoFailure);
        }
        return;
      }

      if (this.reelReady && this.visible && !document.hidden && !this.reducedMotion) {
        this.playReel();
      } else if (this.reelReady) {
        this.pauseReel();
      }

      if (this.visible && !document.hidden && !this.reelActive) {
        this.schedule();
      } else {
        window.clearTimeout(this.timer);
      }
    }

    onVisibilityChange() {
      this.sync();
    }

    destroy() {
      this.destroyed = true;
      window.clearTimeout(this.timer);
      window.clearTimeout(this.transitionTimer);
      window.clearTimeout(this.videoFallbackTimer);
      window.clearTimeout(this.videoStallTimer);
      window.clearTimeout(this.reelLoadTimer);
      window.clearTimeout(this.imagePreloadTimer);
      window.clearTimeout(this.secondaryImagePreloadTimer);
      this.intersectionObserver?.disconnect();

      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener(CINEMA_START_EVENT, this.onCinemaStart);
      document.removeEventListener(CINEMA_COMPLETE_EVENT, this.onCinemaComplete);

      this.video?.removeEventListener('playing', this.onVideoPlaying);
      this.video?.removeEventListener('ended', this.onVideoEnded);
      this.video?.removeEventListener('error', this.onVideoFailure);
      this.video?.removeEventListener('abort', this.onVideoFailure);
      this.video?.removeEventListener('waiting', this.onVideoWaiting);
      this.video?.removeEventListener('stalled', this.onVideoWaiting);
      this.video?.pause();

      this.reelControls.forEach((control) => {
        control.removeEventListener('click', this.onReelControlClick);
      });
      this.reelVideos.forEach((video) => {
        video.removeEventListener('playing', this.onReelPlaying);
        video.removeEventListener('ended', this.onReelEnded);
        video.removeEventListener('error', this.onReelFailure);
        video.removeEventListener('abort', this.onReelFailure);
        video.pause();
      });
      delete this.root.dataset.galleryReady;
    }
  }

  const galleries = new WeakMap();
  const mount = (scope = document) => {
    scope.querySelectorAll('[data-hagenuk-hero-gallery]').forEach((root) => {
      if (galleries.has(root)) return;
      const gallery = new HagenukHeroGallery(root);
      galleries.set(root, gallery);
      gallery.init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-hagenuk-hero-gallery]').forEach((root) => {
      galleries.get(root)?.destroy();
      galleries.delete(root);
    });
  });
})();
